import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, switchMap } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { environment as env } from '../../environments/environment';
import { AuthHttpClient } from '../http/AuthHttpClient';
import { Store } from '../store';
import { ApiService } from './api.service';
import { CookieService } from './cookie.service';
import { StorageService } from './storage.service';
import { Socket } from 'ngx-socket-io';
import { User, UserEvent, UserEventData } from '../shared/model/model';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    private getUsersByAccountType$: ReplaySubject<any> = null;
    private user: User = this.store.selectForLocal<User>('user');
    private systemSocket;

    constructor(
        private apiService: ApiService,
        private http: AuthHttpClient,
        private store: Store,
        private cookieService: CookieService,
        private storageService: StorageService
    ) {
        this.systemSocket = new Socket({ url: `${env.socketPath}system` }); // system chanel

        this.systemSocket.on('connect', () => {
            this.systemSocket
                .fromEvent(UserEvent.Updated)
                .pipe(
                    filter((item: UserEventData) => item.userId === this.user.id),
                    switchMap(() => this.getByToken())
                )
                .subscribe();
        });
    }

    getColours() {
        return this.http.get(env.apiPath + 'users/colours', this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    getByToken() {
        return this.http.get(env.apiPath + 'users/profile').pipe(
            map(({ data }: any) => {
                if (!data || !data.id) {
                    this.cookieService.remove();
                    this.storageService.remove();
                    this.store.set('token', '');
                    this.store.set('loggedIn', false);
                    this.store.set('user', undefined);

                    return null;
                }

                this.store.set('user', data);
                this.storageService.setUser(data);
                this.cookieService.setUser(data);
                return data;
            })
        );
    }

    findOne(id) {
        return this.http.get(env.apiPath + 'users/' + id, this.apiService.getHttpOptions()).pipe(
            map((data: any) => data),
            catchError(this.apiService.handleError)
        );
    }

    getAdmins() {
        return this.http.get(env.apiPath + 'users/admins', this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    delete(id) {
        return this.http.delete(env.apiPath + 'users/' + id, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    async createUserNew(user, action?) {
        user.source = user.source || 'admin';
        const dataToSend = {
            user,
            createdBy: this.store.selectForLocal('user'),
            action,
        };

        const result: any = await this.http
            .post(env.apiPath + 'users/new', dataToSend, this.apiService.getHttpOptions())
            .toPromise();
        return result.data;
    }

    importGoogleContacts(importSource: string): Observable<any> {
        return this.http
            .post<{ data: { created: boolean } }>(
                env.apiPath + 'google-contacts/run-main-data',
                { importSource },
                this.apiService.getHttpOptions()
            )
            .pipe(map(({ data }) => data));
    }

    async createAddressNew(address, user) {
        const dataToSend = {
            address,
            user,
            createdBy: this.store.selectForLocal('user'),
        };
        const result: any = await this.http
            .post(env.apiPath + 'users/address/new', dataToSend, this.apiService.getHttpOptions())
            .toPromise();

        return result.data;
    }

    async createCollectionAddress(address, user) {
        const dataToSend = {
            address,
            user,
            isCollection: 1,
        };
        const result: any = await this.http
            .post(env.apiPath + 'users/address/new', dataToSend, this.apiService.getHttpOptions())
            .toPromise();

        return result.data;
    }

    deleteAddress(id) {
        return this.http.delete(env.apiPath + 'users/address/' + id, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    async createCompanyNew(company, user) {
        const dataToSend = {
            company,
            user,
            createdBy: this.store.selectForLocal('user'),
        };
        const result: any = await this.http
            .post(env.apiPath + 'users/company/new', dataToSend, this.apiService.getHttpOptions())
            .toPromise();
        return result.data;
    }

    findAddressesByCustomer(userId) {
        return this.http.get(env.apiPath + 'users/addresses/' + userId, this.apiService.getHttpOptions()).pipe(
            map((data: any) => data),
            catchError(this.apiService.handleError)
        );
    }

    update(user) {
        return this.http.put(env.apiPath + 'users/' + user.id, user, this.apiService.getHttpOptions()).pipe(
            map(async (data: any) => {
                // this.store.set('user', user);
                // await this.storageService.setUser(user);

                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    updateUserField(field, value) {
        return this.http.patch(env.apiPath + `users/user/field/${field}`, { value });
    }

    updateUserContactField(field, value) {
        return this.http.patch(env.apiPath + `users/contact/field/${field}`, { value });
    }

    add2Favorite(user) {
        return this.http
            .patch(env.apiPath + `users/${user.id}/favorite`, {}, this.apiService.getHttpOptions())
            .pipe(map(async (data: any) => data));
    }

    rmFavorite(user) {
        return this.http
            .delete(env.apiPath + `users/${user.id}/favorite`, this.apiService.getHttpOptions())
            .pipe(map(async (data: any) => data));
    }

    create(user) {
        user.source = 'admin';
        return this.http.post(env.apiPath + 'users/signup', user, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    passwordReset(user) {
        const dataToSend = {
            email: user.contact.email,
        };
        return this.http.post(env.apiPath + 'users/password/reset', dataToSend).pipe(map((data: any) => data));
    }

    adminPasswordReset(user) {
        const dataToSend = {
            email: user.contact.email,
        };
        return this.http.post(env.apiPath + 'users/admin/password/reset', dataToSend).pipe(map((data: any) => data));
    }

    search(params) {
        const dataToSend = {
            params,
        };
        return this.http.post(env.apiPath + 'users/search', dataToSend, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    getUsersByAccountType() {
        if (!this.getUsersByAccountType$) {
            this.getUsersByAccountType$ = new ReplaySubject<any>();
            this.http
                .get(env.apiPath + 'users/byAccountType')
                .subscribe((data) => this.getUsersByAccountType$.next(data));
        }

        return this.getUsersByAccountType$;
    }

    searchCustomers(params) {
        return this.http
            .post(env.apiPath + 'users/search/customers', { params }, this.apiService.getHttpOptions())
            .pipe(map((data: any) => data));
    }

    searchCustomersTrade(params) {
        // not that big of the difference from the top function
        return this.http
            .post(env.apiPath + 'users/search/customers/trade', { params })
            .pipe(map(({ data }: any) => data));
    }

    getAllPermissions(accountType) {
        const dataToSend = {
            accountType,
        };
        return this.http.post(env.apiPath + 'users/permissions/all', dataToSend, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    getUserPermissions(userId) {
        return this.http.get(env.apiPath + 'users/permissions/user/' + userId, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    updateUserPermissions(userId, permissions) {
        const dataToSend = {
            userId,
            permissions,
        };
        return this.http
            .post(env.apiPath + 'users/permissions/user/', dataToSend, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    searchSupplierCollectionCosts(params) {
        const dataToSend = {
            params,
        };
        return this.http
            .post(env.apiPath + 'courier/supplier/collection/', dataToSend, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    updateSupplierCollectionCosts(supplier, courierId) {
        const dataToSend = {
            courierId,
            supplier,
        };

        return this.http
            .put(env.apiPath + 'courier/supplier/collection', dataToSend, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }

    deleteCompany(companyId) {
        return this.http.delete(env.apiPath + 'users/company/' + companyId, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    async postcodeLookup(postcode) {
        const result: any = await this.http
            .get(`${env.apiPath}postcode/${postcode}`, this.apiService.getHttpOptions())
            .pipe(map((data: any) => data.data))
            .toPromise();

        return result;
    }

    searchCompany(params) {
        const dataToSend = {
            params,
        };
        return this.http
            .post(env.apiPath + 'users/company/type/search', dataToSend, this.apiService.getHttpOptions())
            .pipe(
                map((data: any) => {
                    return data;
                }, catchError(this.apiService.handleError))
            );
    }
    createCompanyType(user) {
        return this.http.post(env.apiPath + 'users/company/type', user, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }
    updateCompanyType(user) {
        return this.http.put(env.apiPath + 'users/company/type', user, this.apiService.getHttpOptions()).pipe(
            map(async (data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }
    deleteCompanyType(id) {
        return this.http.delete(env.apiPath + 'users/company/type/' + id, this.apiService.getHttpOptions()).pipe(
            map((data: any) => {
                return data;
            }, catchError(this.apiService.handleError))
        );
    }

    userOnline(userId) {
        const onlineUsers = this.store.selectForLocal('onlineUsers');
        if (onlineUsers.indexOf(userId) < 0) {
            return false;
        }
        return true;
    }

    all() {
        return this.http
            .get(env.apiPath + 'users')
            .pipe(tap((data: any) => this.store.set('allUsers', data.data)));
    }

    validateDuplication(userData) {
        return this.http
            .post(env.apiPath + 'users/check-on-duplication', userData, this.apiService.getHttpOptions())
            .pipe(catchError(this.apiService.handleError));
    }

    checkUserExist(params: { email: string; mobile: string }) {
        return this.http
            .post(env.apiPath + 'users/check-user-exists-by-email-or-mobile', params, this.apiService.getHttpOptions())
            .pipe(catchError(this.apiService.handleError));
    }
}
